import React, { useCallback, useState } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';

import { cssMap } from '@atlaskit/css';

// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Text from '@atlaskit/primitives/text';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Stack from '@atlaskit/primitives/stack';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Inline from '@atlaskit/primitives/inline';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import { xcss } from '@atlaskit/primitives/xcss';
// oxlint-disable-next-line @atlaskit/design-system/no-emotion-primitives
import Box from '@atlaskit/primitives/box';
import IconButton from '@atlaskit/button/icon/button';
import CopyIcon from '@atlaskit/icon/core/copy';
import Tooltip from '@atlaskit/tooltip/Tooltip';
import ButtonItem from '@atlaskit/menu/button-item';
import Lozenge from '@atlaskit/lozenge/lozenge';

import { fg } from '@confluence/feature-gating';

import type { useVersionHistoryContentVersionHistoryQuery_contentVersionHistory_ContentVersionHistoryConnection_nodes_ContentVersion as VersionHistoryNodeType } from './__types__/useVersionHistoryContentVersionHistoryQuery.ts';
import { getFormattedDate } from './versionHistoryUtils.ts';

const i18n = defineMessages({
	currentVersionDescription: {
		id: 'version-history.current.version.description',
		defaultMessage: 'Most recently saved',
		description:
			'The text is shown as a subtitle below the content title in the current version item at the top of the version history list, indicating it is the latest saved state.',
	},
	lastEditedByLabel: {
		id: 'version-history.author.last-edited-by.label',
		defaultMessage: 'Last edited by {author}',
		description: 'Display text to show who edited a version of a whiteboard',
	},
	lastEditedByAnonymousUserLabel: {
		id: 'version-history.author.last-edited-by-anonymous-user.label',
		defaultMessage: 'Last edited by Anonymous',
		description: 'Display text to show an anonymous user edited a version of a whiteboard',
	},
	lastEditedByDeactivatedUserLabel: {
		id: 'version-history.author.last-edited-by-deactivated-user.label',
		defaultMessage: 'Last edited by {author} (Deactivated)',
		description: 'Display text to show a deactivated user edited a version of a whiteboard',
	},
});

export const versionItemMessages: Record<
	| 'copyIconLabel'
	| 'copyIconTooltip'
	| 'copyIconTooltipCopied'
	| 'versionContainsAIContentTooltip'
	| 'aiContentText',
	MessageDescriptor
> = defineMessages({
	copyIconLabel: {
		id: 'version-history.copy.icon.label',
		defaultMessage: 'Copy icon',
		description:
			'The text is used as the accessible aria-label for the copy icon button shown on a selected version history item. Clicking it copies all content from that version.',
	},
	copyIconTooltip: {
		id: 'version-history.copy.icon.tooltip',
		defaultMessage: 'Copy all content',
		description: 'tooltip text displayed on the copy icon before clicking',
	},
	copyIconTooltipCopied: {
		id: 'version-history.copy.icon.tooltip-copied',
		defaultMessage: 'Copied!',
		description: 'tooltip text displayed on the copy button after user has clicked',
	},
	versionContainsAIContentTooltip: {
		id: 'version-history.lozenge.version-contains-ai-content.tooltip',
		defaultMessage: 'This version includes AI generated content',
		description:
			'Tooltip text displayed on the version history item when the version contains AI content',
	},
	aiContentText: {
		id: 'version-history.lozenge.ai-content-text',
		defaultMessage: 'AI Generated',
		description: 'Text displayed on the version history item when the version contains AI content',
	},
});

const styles = cssMap({
	customStylesContainer: {
		width: '174px',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
	},
});

const itemContainerStyles = xcss({
	paddingLeft: 'space.100',
});

type VersionHistoryNodeProps = {
	handleVersionClick: (version: VersionHistoryNodeType | null) => void;
	isSelected: boolean;
};

type VersionHistoryItemProps = {
	version: VersionHistoryNodeType;
	isCopyAllowed: boolean;
	handleCopyClick: () => void;
} & VersionHistoryNodeProps;

type VersionHistoryCurrentVersionItemProps = {
	title: string;
} & VersionHistoryNodeProps;

export const VersionHistoryCurrentVersionItem = ({
	handleVersionClick,
	isSelected,
	title,
}: VersionHistoryCurrentVersionItemProps): React.JSX.Element => {
	return (
		<ButtonItem
			isSelected={isSelected}
			testId="version-history-item"
			onClick={() => handleVersionClick(null)}
		>
			<Inline alignBlock="center" spread="space-between" xcss={itemContainerStyles}>
				<Stack space="space.050">
					<Text size="medium">{title}</Text>
					<Text size="small" color="color.text.subtle">
						<FormattedMessage {...i18n.currentVersionDescription} />
					</Text>
				</Stack>
			</Inline>
		</ButtonItem>
	);
};

export const VersionHistoryItem = ({
	version,
	handleVersionClick,
	isSelected,
	isCopyAllowed,
	handleCopyClick,
}: VersionHistoryItemProps): React.JSX.Element => {
	const { formatMessage, formatDate, formatTime } = useIntl();
	const versionContainsAIContentTooltip = formatMessage(
		versionItemMessages.versionContainsAIContentTooltip,
	);
	const aiContentText = formatMessage(versionItemMessages.aiContentText);
	const copyAllContentText = formatMessage(versionItemMessages.copyIconTooltip);
	const copiedText = formatMessage(versionItemMessages.copyIconTooltipCopied);
	const [isCopyClicked, setIsCopyClicked] = useState(false);

	const handleClick = useCallback(
		(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			e.stopPropagation();
			handleCopyClick();
			setIsCopyClicked(true);
		},
		[handleCopyClick],
	);

	const handleMouseLeave = () => {
		setIsCopyClicked(false);
	};

	const displayName = () => {
		const author = version?.author;

		if (!author?.id) {
			return <FormattedMessage {...i18n.lastEditedByAnonymousUserLabel} />;
		}

		if (author?.isActive) {
			return (
				<FormattedMessage {...i18n.lastEditedByLabel} values={{ author: author?.displayName }} />
			);
		}

		return (
			<FormattedMessage
				{...i18n.lastEditedByDeactivatedUserLabel}
				values={{ author: author?.displayName }}
			/>
		);
	};

	const shouldShowCopyIcon = isSelected && isCopyAllowed;
	const versionContainsAIContent = version.contentProperties?.latest?.versionContainsAIContent;

	return (
		<ButtonItem
			isSelected={isSelected}
			testId="version-history-item"
			onClick={() => handleVersionClick(version)}
		>
			<Inline alignBlock="center" spread="space-between" xcss={itemContainerStyles}>
				<Stack space="space.050">
					{/* oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates */}
					{fg('whiteboard-show-ai-contributor-version-history') ? (
						<Inline space="space.100">
							<Box xcss={styles.customStylesContainer}>
								<Text size="medium">
									{getFormattedDate(version.updatedTime, formatDate, formatTime)}
								</Text>
							</Box>
							<Tooltip content={versionContainsAIContentTooltip}>
								{versionContainsAIContent && (
									<Lozenge maxWidth={95} appearance="neutral">
										{aiContentText}
									</Lozenge>
								)}
							</Tooltip>
						</Inline>
					) : (
						<Text size="medium">
							{getFormattedDate(version.updatedTime, formatDate, formatTime)}{' '}
						</Text>
					)}
					<Text size="small" color="color.text.subtle">
						{displayName()}
					</Text>
				</Stack>
				{shouldShowCopyIcon && (
					<Tooltip
						content={isCopyClicked ? copiedText : copyAllContentText}
						position="bottom"
						// force remount otherwise content is off-center
						key={isCopyClicked ? 'copied' : 'default'}
					>
						{(tooltipProps) => (
							<IconButton
								{...tooltipProps}
								appearance="subtle"
								icon={CopyIcon}
								label={formatMessage(versionItemMessages.copyIconLabel)}
								onClick={handleClick}
								onMouseLeave={handleMouseLeave}
								onBlur={handleMouseLeave}
								testId="version-content-copy-button"
							/>
						)}
					</Tooltip>
				)}
			</Inline>
		</ButtonItem>
	);
};
